// Libs
import classNames from 'classnames/bind';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid2, Stack } from '@mui/material';
// Components, Layouts, Pages
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import { BaseInput, BaseSelect, Loading, UnderDevelopment } from '~/components/common';
// Others
import { defaultValuesForm, schema } from './helper';
import { useAppDispatch } from '~/utils/redux/hooks';
import { BACK_TO_PREVIOUS_PAGE, EMPTY_STRING, REG_EXP } from '~/utils/constants/common';
import { IFormCampaign } from '~/utils/interface/campaign';
import { mockDataCampaignType } from '~/utils/mockData/campaign';
import { IBaseOption } from '~/utils/interface/common';
import { TypeCampaignEnum } from '~/utils/enum/common';
import { compareDataUpdate, removeEmptyObjects } from '~/utils/helper/common';
import { createCampaign, getDetailCampaign, updateCampaign } from '~/thunks/campaign/campaignThunk';
// Styles, images, icons
import styles from './FormCampaign.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const FormCampaign = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/campaign', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: campaignId } = useParams();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    trigger,
    formState: { errors, isDirty },
  } = useForm<IFormCampaign>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });

  const type = watch('type');
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [initialData, setInitialData] = useState<IFormCampaign>(defaultValuesForm);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const handleGetCampaignDetail = useCallback(
    (campaignId: string) => {
      if (!campaignId) return;

      setIsLoading(true);

      dispatch(getDetailCampaign(campaignId))
        .unwrap()
        .then((res) => {
          const campaignDetail = res?.data;

          const newData: IFormCampaign = {
            days: String(campaignDetail?.days) || EMPTY_STRING,
            name: campaignDetail?.name || EMPTY_STRING,
            type: campaignDetail?.type || EMPTY_STRING,
            content: campaignDetail?.content || EMPTY_STRING,
            subject: campaignDetail?.subject || EMPTY_STRING,
          };

          setInitialData(newData);
          reset(newData);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, reset]
  );

  //#region Implement Hook
  useEffect(() => {
    if (!type) return;

    type === TypeCampaignEnum.BEFORE_JOB_STARTS && trigger('content');
    type === TypeCampaignEnum.BEFORE_JOB_STARTS && setValue('subject', EMPTY_STRING);
  }, [type, trigger, setValue]);

  useEffect(() => {
    if (!campaignId) return;

    handleGetCampaignDetail(campaignId);
  }, [campaignId, handleGetCampaignDetail]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSubmitForm = (data: IFormCampaign) => {
    if (campaignId) {
      handleEditCampaign(data);
    } else {
      handleAddCampaign(data);
    }
  };

  const handleAddCampaign = (data: IFormCampaign) => {
    const formattedData: IFormCampaign = removeEmptyObjects(data) as IFormCampaign;

    setIsLoading(true);

    dispatch(createCampaign(formattedData))
      .unwrap()
      .then((res) => {
        handleCancelForm();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditCampaign = (data: IFormCampaign) => {
    const dataChange: IFormCampaign = compareDataUpdate(initialData, data) as IFormCampaign;

    if (!campaignId || !dataChange) return;

    setIsLoading(true);

    dispatch(updateCampaign({ campaignId, body: dataChange }))
      .unwrap()
      .then((res) => {
        handleCancelForm();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCancelForm = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
    reset(defaultValuesForm);
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='formCampaignPage'
      title={t(campaignId ? 'form_campaign_edit_title' : 'form_campaign_add_title')}
      onBackToPreviousPage={handleCancelForm}
    >
      <Stack
        component={'form'}
        spacing={'20px'}
        onSubmit={handleSubmit(handleSubmitForm)}
        className={cx('form')}
      >
        <Box component={'div'} className={cx('formBody')}>
          <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
            <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
              <Grid2 size={6}>
                <Controller
                  name='type'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseSelect
                      label={t('form_campaign_label_type')}
                      value={value}
                      options={mockDataCampaignType || []}
                      onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
                      errorMessage={errors.type?.message}
                      required
                    />
                  )}
                />
              </Grid2>
            </Grid2>

            {type && (
              <>
                <Grid2 container size={12} rowSpacing={'24px'} columnSpacing={'12px'}>
                  <Grid2 size={6}>
                    <Controller
                      name='name'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInput
                          label={t('form_campaign_label_name')}
                          value={value}
                          onChange={onChange}
                          errorMessage={errors?.name?.message}
                          required
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <Controller
                      name='days'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInput
                          label={t(
                            type === TypeCampaignEnum.BEFORE_JOB_STARTS
                              ? 'form_campaign_label_date_before_job_starts'
                              : 'form_campaign_label_date_after_job_complete'
                          )}
                          value={value}
                          onChange={(event) => {
                            const inputValue = event.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                            const number = inputValue ? Number(inputValue).toString() : EMPTY_STRING;
                            onChange(number);
                          }}
                          errorMessage={errors?.days?.message}
                          required
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>

                {type === TypeCampaignEnum.AFTER_JOB_COMPLETES && (
                  <Grid2 size={12}>
                    <Controller
                      name='subject'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInput
                          label={t('form_campaign_label_subject')}
                          value={value}
                          onChange={onChange}
                          errorMessage={errors?.subject?.message}
                          required={type === TypeCampaignEnum.AFTER_JOB_COMPLETES}
                        />
                      )}
                    />
                  </Grid2>
                )}

                <Grid2 size={12}>
                  <Controller
                    name='content'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseInput
                        multiline
                        height={'auto'}
                        rows={6}
                        label={t('form_campaign_label_content')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors?.content?.message}
                        required={type === TypeCampaignEnum.AFTER_JOB_COMPLETES}
                      />
                    )}
                  />
                </Grid2>
              </>
            )}
          </Grid2>
        </Box>

        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'flex-end'}
          component={'div'}
          spacing={'12px'}
          className={cx('formFooter')}
        >
          <Button variant='outlined' onClick={handleCancelForm}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button variant='contained' type='submit' disabled={campaignId ? !isDirty : false}>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </Stack>

        {isLoading && <Loading />}

        <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
      </Stack>
    </ChildrenPage>
  );
};

export default FormCampaign;
